import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import ReactFlow, { Background, Controls, MiniMap, addEdge, applyEdgeChanges, applyNodeChanges } from 'reactflow';
import { WorkFlowSimple, allFilters, exampleActions, exampleTriggers, getAllowedConnections, nodeTypes } from './constants';
// If you have modularized node components, forms, and test panel, import them as needed:
// import { TriggerNode, ActionNode, FilterNode, DestinationNode } from './WorkflowNodes';
// import { FilterPropertiesForm, DestinationPropertiesForm } from './WorkflowForms';
// import TestPanel from './TestPanel';
export default function WorkFlowCanvas() {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [activeTab, setActiveTab] = useState('triggers');
    const [selectedNode, setSelectedNode] = useState(null);
    const [sideDrawerOpen, setSideDrawerOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [sourceNode, setSourceNode] = useState(null);
    const [testPanelOpen, setTestPanelOpen] = useState(false);
    const reactFlowWrapper = useRef(null);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const sideDrawerRef = useRef(null);

    // Handle Test button click
    const handleTestButtonClick = () => {
        setTestPanelOpen(true);
    };
    // Handle outside click for side drawer
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sideDrawerOpen && sideDrawerRef.current && !sideDrawerRef.current.contains(event.target)) {
                setSideDrawerOpen(false);
            }
        };

        if (sideDrawerOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [sideDrawerOpen]);

    // Handle output handle click
    const handleOutputClick = useCallback((event, node) => {
        event.stopPropagation(); // Prevent triggering other click events
        setSourceNode(node);
        setShowModal(true);
    }, []);

    // Add node to canvas (use correct nodeType key)
    const addNode = useCallback((nodeType, item, position = { x: 100, y: 100 }) => {
        // Validate nodeType
        if (!nodeTypes[nodeType]) {
            console.error(`Invalid node type: ${nodeType}. Available types:`, Object.keys(nodeTypes));
            return;
        }

        const id = `${nodeType}-${item.id}-${Date.now()}`;
        const newNode = {
            id,
            type: nodeType,
            data: {
                label: item.name,
                description: item.description,
                icon: item.icon,
                position: position,
                onOutputClick: handleOutputClick
            },
            position,
        };

        console.log('Adding node:', newNode);

        setNodes(nds => [...nds, newNode]);
    }, [handleOutputClick]);

    // Drag and drop from menu bar (use nodeType)
    const onDragStart = (event, nodeType, item) => {
        event.dataTransfer.setData('application/reactflow', JSON.stringify({ nodeType, item }));
        event.dataTransfer.effectAllowed = 'move';
    };

    const onDrop = useCallback((event) => {
        event.preventDefault();
        const data = event.dataTransfer.getData('application/reactflow');
        if (!data) return;
        const { nodeType, item } = JSON.parse(data);
        const position = reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        });
        addNode(nodeType, item, position);
    }, [reactFlowInstance, addNode]);

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    // Prevent connections into trigger nodes
    const onConnect = useCallback((params) => {
        // Find the target node
        const targetNode = nodes.find(n => n.id === params.target);
        if (targetNode && targetNode.type === 'triggerNode') {
            // Do not allow connection into trigger node
            return;
        }
        setEdges((eds) => addEdge(params, eds));
    }, [nodes]);

    const onNodesChange = useCallback((changes) => {
        setNodes((nds) => applyNodeChanges(changes, nds));
    }, []);

    const onEdgesChange = useCallback((changes) => {
        setEdges((eds) => applyEdgeChanges(changes, eds));
    }, []);

    // Handle node clicks - open side drawer for filter and destination nodes
    const onNodeClick = useCallback((event, node) => {
        if (node.type === 'filterNode' || node.type === 'destinationNode') {
            setSelectedNode(node);
            setSideDrawerOpen(true);
        }
    }, []);

    // Close modal
    const handleModalClose = () => {
        setShowModal(false);
        setSourceNode(null);
    };

    // Select node type from modal
    const handleNodeTypeSelect = (nodeType, selectedItem = null) => {
        if (!nodeTypes[nodeType]) {
            console.error(`Invalid node type: ${nodeType}`);
            return;
        }

        let nodeData = {
            label: `${nodeType.replace('Node', '')} Node`,
            description: `A new ${nodeType.replace('Node', '')} node`,
            position: { x: sourceNode.position.x + 200, y: sourceNode.position.y },
            onOutputClick: handleOutputClick
        };

        // If a specific item was selected (like a filter or action), use its data
        if (selectedItem) {
            nodeData = {
                ...nodeData,
                label: selectedItem.name,
                description: selectedItem.description,
                icon: selectedItem.icon,
                filterType: selectedItem.filterType,
                category: selectedItem.category,
                onOutputClick: handleOutputClick
            };
        }

        const newNode = {
            id: `${nodeType}-${selectedItem?.id || 'generic'}-${Date.now()}`,
            type: nodeType,
            data: nodeData,
            position: { x: sourceNode.position.x + 200, y: sourceNode.position.y },
        };

        setNodes((nds) => [...nds, newNode]);
        setEdges((eds) => [...eds, { id: `e${sourceNode.id}-${newNode.id}`, source: sourceNode.id, target: newNode.id }]);
        handleModalClose();

        // If the new node is a filter or destination node, immediately open its properties
        if (nodeType === 'filterNode' || nodeType === 'destinationNode') {
            setTimeout(() => {
                setSelectedNode(newNode);
                setSideDrawerOpen(true);
            }, 100); // Small delay to ensure the node is rendered
        }

        // For action nodes, you could add similar property modals here in the future
        // if (nodeType === 'actionNode') {
        //     // Open action properties modal
        // }
    };

    // Export workflow to JSON
    const exportWorkflow = useCallback(() => {
        const workflowData = {
            nodes: nodes.map(node => ({
                id: node.id,
                type: node.type,
                position: node.position,
                data: node.data
            })),
            edges: edges.map(edge => ({
                id: edge.id,
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
                targetHandle: edge.targetHandle
            })),
            metadata: {
                exportedAt: new Date().toISOString(),
                version: '1.0.0',
                totalNodes: nodes.length,
                totalEdges: edges.length
            }
        };

        const jsonString = JSON.stringify(workflowData, null, 2);
        console.log('Exported Workflow:', jsonString);

        // Optional: Also show a brief summary in console
        console.log('Workflow Summary:', {
            nodes: nodes.length,
            edges: edges.length,
            nodeTypes: [...new Set(nodes.map(n => n.type))]
        });
        WorkFlowSimple.UpsertWorkFlowConfig(workflowData)
            .then(response => {
                console.log('Workflow Config Saved:', response);
            })
            .catch(error => {
                console.error('Error Saving Workflow Config:', error);
            });
    }, [nodes, edges]);

    // Menu bar tabs
    const menuTabs = [
        { key: 'triggers', label: 'Triggers', items: exampleTriggers },
        { key: 'actions', label: 'Actions', items: exampleActions },
        { key: 'filters', label: 'Filters', items: allFilters },
    ];

    // Map tab key to nodeType key
    const tabToNodeType = {
        triggers: 'triggerNode',
        actions: 'actionNode',
        filters: 'filterNode',
    };

    return (
        <div style={{ height: '700px', width: '100%', background: '#f4f8fc', padding: 0 }}>
            <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', padding: '0 24px', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {menuTabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                border: 'none',
                                background: activeTab === tab.key ? '#007bff' : 'transparent',
                                color: activeTab === tab.key ? '#fff' : '#007bff',
                                fontWeight: 'bold',
                                fontSize: 16,
                                padding: '12px 24px',
                                cursor: 'pointer',
                                borderRadius: '8px 8px 0 0',
                                marginRight: 8,
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                        onClick={handleTestButtonClick}
                        style={{
                            background: '#ffc107',
                            color: '#333',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        title="Test workflow"
                    >
                        🧪 Test
                    </button>
                    <button
                        onClick={exportWorkflow}
                        style={{
                            background: '#28a745',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        title="Export workflow to JSON"
                    >
                        📤 Export
                    </button>
                </div>
            </div>
            <div style={{ background: '#f8faff', padding: '12px 24px', display: 'flex', gap: 16, borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap', maxHeight: '120px', overflowY: 'auto' }}>
                {(menuTabs.find(tab => tab.key === activeTab)?.items ?? []).map(item => (
                    <div
                        key={item.id}
                        draggable
                        onDragStart={e => onDragStart(e, tabToNodeType[activeTab], item)}
                        onClick={() => addNode(tabToNodeType[activeTab], item)}
                        style={{
                            background: '#fff',
                            border: '1px solid #007bff',
                            borderRadius: 6,
                            padding: '10px 16px',
                            minWidth: 120,
                            maxWidth: 200,
                            cursor: 'grab',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            transition: 'box-shadow 0.2s',
                        }}
                        title="Click or drag to add to workflow"
                    >
                        <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 14 }}>{item.type}</div>
                        <div style={{ fontSize: 12, color: '#555', lineHeight: '1.3' }}>{item.description}</div>
                    </div>
                ))}
            </div>
            <div ref={reactFlowWrapper} style={{ height: '520px', width: '100%', position: 'relative', background: '#f4f8fc' }}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onConnect={onConnect}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={onNodeClick}
                    nodeTypes={nodeTypes} // Ensure custom node types are registered
                    fitView
                    onInit={setReactFlowInstance}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                >
                    <MiniMap
                        nodeColor="#666"
                        maskColor="rgba(240, 240, 240, 0.6)"
                    />
                    <Controls />
                    <Background color="#aaa" gap={16} />
                </ReactFlow>
            </div>


            {/* Test Panel Sidebar */}
            {testPanelOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '400px',
                    height: '100%',
                    background: '#fff',
                    boxShadow: '-2px 0 12px rgba(0,0,0,0.08)',
                    zIndex: 9999,
                    padding: '32px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                        <h4 style={{ margin: 0 }}>Test Workflow Module</h4>
                        <Button variant="outline-secondary" size="sm" onClick={() => setTestPanelOpen(false)}>
                            Close
                        </Button>
                    </div>
                    {/* Add your test panel form and logic here */}
                    <div>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Module Name</Form.Label>
                                <Form.Control type="text" placeholder="Enter module name" />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Trigger Type</Form.Label>
                                <Form.Select>
                                    <option value="">Select trigger type</option>
                                    <option value="manual">Manual</option>
                                    <option value="scheduled">Scheduled</option>
                                </Form.Select>
                            </Form.Group>
                            {/* Dynamic parameters UI can be added here */}
                            <Button variant="primary" type="submit">Run Test</Button>
                        </Form>
                    </div>
                </div>
            )}

            {/* Modal for selecting node type */}
            <Modal show={showModal} onHide={handleModalClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Connect to {sourceNode?.data?.label || 'Node'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {sourceNode && (() => {
                        const allowedConnections = getAllowedConnections(sourceNode);
                        const hasFilters = allowedConnections.allowedFilters && allowedConnections.allowedFilters.length > 0;
                        const hasActions = allowedConnections.allowedActions && allowedConnections.allowedActions.length > 0;
                        const hasDestinations = allowedConnections.allowedDestinations && allowedConnections.allowedDestinations.length > 0;

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div style={{ color: '#666', fontSize: '14px' }}>
                                    Choose what to connect to <strong>{sourceNode.data.label}</strong>:
                                </div>

                                {/* Show Filter Options */}
                                {hasFilters && (
                                    <div>
                                        <h6 style={{ marginBottom: '12px', color: '#333', fontWeight: 'bold' }}>
                                            🔍 Available Filters
                                        </h6>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '8px' }}>
                                            {allowedConnections.allowedFilters.map((filter) => (
                                                <Button
                                                    key={filter.id}
                                                    variant="outline-warning"
                                                    onClick={() => handleNodeTypeSelect('filterNode', filter)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        gap: '12px',
                                                        padding: '12px 16px',
                                                        textAlign: 'left',
                                                        height: 'auto',
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    <span style={{ fontSize: '20px' }}>{filter.icon}</span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{filter.name}</div>
                                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>{filter.description}</div>
                                                    </div>
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Show Action Options */}
                                {hasActions && (
                                    <div>
                                        <h6 style={{ marginBottom: '12px', color: '#333', fontWeight: 'bold' }}>
                                            ⚡ Available Actions
                                        </h6>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '8px' }}>
                                            {allowedConnections.allowedActions.map((action) => (
                                                <Button
                                                    key={action.id}
                                                    variant="outline-success"
                                                    onClick={() => handleNodeTypeSelect('actionNode', action)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        gap: '12px',
                                                        padding: '12px 16px',
                                                        textAlign: 'left',
                                                        height: 'auto',
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    <span style={{ fontSize: '20px' }}>{action.icon}</span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{action.name}</div>
                                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>{action.description}</div>
                                                    </div>
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Show Destination Options */}
                                {hasDestinations && (
                                    <div>
                                        <h6 style={{ marginBottom: '12px', color: '#333', fontWeight: 'bold' }}>
                                            🎯 Available Destinations
                                        </h6>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                                            {allowedConnections.allowedDestinations.map((destination) => (
                                                <Button
                                                    key={destination.id}
                                                    variant="outline-info"
                                                    onClick={() => handleNodeTypeSelect('destinationNode', destination)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        gap: '12px',
                                                        padding: '12px 16px',
                                                        textAlign: 'left',
                                                        height: 'auto',
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    <span style={{ fontSize: '20px' }}>{destination.icon}</span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{destination.name}</div>
                                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>{destination.description}</div>
                                                    </div>
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Show message if no connections are allowed */}
                                {!hasFilters && !hasActions && !hasDestinations && (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '32px',
                                        color: '#666',
                                        background: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px dashed #dee2e6'
                                    }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>No connections available</div>
                                        <div style={{ fontSize: '14px' }}>
                                            This node type doesn't support outgoing connections, or the configuration needs to be updated.
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </Modal.Body>
            </Modal>

            <div style={{ padding: '16px 24px', background: '#fff', borderTop: '1px solid #e0e0e0' }}>
                <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
                    Use the menu above to add triggers, actions, or filters to your workflow.
                    Click or drag items to add nodes, then connect them visually to create your workflow.
                    Click on filter or destination nodes to edit their properties.
                </p>
            </div>
        </div>
    );
}
